import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, SendaNpc, VentRules, VentState } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { shootTick } from './workshop.ts';

/**
 * The deep road (docs/sendas/artifice.md, chapter 3, act 1, «La calzada honda»; the ground in battle/corazon-layout.ts).
 * The Hondura's tunnel winds down toward the heat, three hexes wide in red rock. Vents across it blow each on its own
 * beat: warned `warn` (the band's floor glows and Grillo's lamp blinks), then blowing `blow`; whoever is in the band
 * while it blows is struck once (`heroBp` of the hero's life, `blows` of a person's) and so is any nightmare in it
 * (`foeBp` of its life: the beat can be used). Grillo goes ahead with Dunna at his side: they stop on the row short of a
 * vent and cross as a blast ends, when the quiet left is enough to be over it; they wait for the hero within `wait`
 * (never inside a band). Ash nightmares and oozes come out of the side holes ahead of them in bands (`foes`), and
 * hunt by the common rule. Dunna shoots. The clan's chalk shows on the wall at three rows. Won with the hero, Grillo and
 * Dunna within `reach` of the Médula's mouth; lost with Dunna or the hero down.
 *
 * Cost: a handful of vents (a modulo each a tick), two people; the nightmares are looked at only as a blast starts.
 */

const fresh = (r: VentRules): VentState => ({ way: 1, cross: -1, burnt: r.vents.map(() => -1), foe: r.foes.first, chalk: 0, foes: 0, end: -1, shot: 0 });

/** Where a vent is in its beat now (0 its blast's first tick). */
export const ventPhase = (s: BattleState, v: VentRules['vents'][number]): number => (s.tick + v.phase) % v.beat;

/** Whether a vent is blowing now. */
export const ventBlowing = (s: BattleState, r: VentRules, v: VentRules['vents'][number]): boolean => ventPhase(s, v) < r.blow;

/** Whether a vent's blast is warned now (its last `warn` ticks before it). */
export const ventWarned = (s: BattleState, r: VentRules, v: VentRules['vents'][number]): boolean => ventPhase(s, v) >= v.beat - r.warn;

/** The vent whose band a y is in (−1: none). */
export const ventAt = (r: VentRules, y: number): number => {
  for (let i = 0; i < r.vents.length; i++) {
    const v = r.vents[i] as VentRules['vents'][number];
    if (y >= v.y0 && y <= v.y1) return i;
  }
  return -1;
};

/** The first vent at or below a y (the one ahead going down; −1 none). */
export const ventAhead = (r: VentRules, y: number): number => {
  for (let i = 0; i < r.vents.length; i++) if ((r.vents[i] as VentRules['vents'][number]).y1 >= y) return i;
  return -1;
};

/** The ticks Grillo needs to be over a vent from the row short of it (its rows and one more, at his pace). */
export const crossTicks = (r: VentRules, v: VentRules['vents'][number]): number => idiv((v.rows + 1) * 866 + r.pace - 1, r.pace) + 6;

const people = (s: BattleState): { g: SendaNpc | undefined; d: SendaNpc | undefined } => ({
  g: s.senda?.npcs.find((n) => n.id === 'grillo'),
  d: s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0),
});

/** The vents' beat: their warnings, and their blasts on what is in their band. */
const blasts = (s: BattleState, r: VentRules, w: VentState, cue: (c: string) => void): void => {
  const { g, d } = people(s);
  for (let i = 0; i < r.vents.length; i++) {
    const v = r.vents[i] as VentRules['vents'][number];
    const ph = ventPhase(s, v);
    if (ph === v.beat - r.warn) {
      ev(s, { t: 'senda', kind: 'ventWarn', npc: 'grillo', x: g?.x ?? 0, y: idiv(v.y0 + v.y1, 2), n: i });
      // (Grillo's lamp blinks for the vent ahead of him.)
      if (g && ventAhead(r, g.y) === i) cue('vent1');
    }
    if (ph >= r.blow) continue;
    const start = s.tick - ph;
    if (ph === 0) {
      ev(s, { t: 'senda', kind: 'ventBlow', npc: '', x: g?.x ?? 0, y: idiv(v.y0 + v.y1, 2), n: i });
      // What is in the band as it blows (the nightmares too: the beat is the road's, not theirs).
      let burnt = false;
      for (const e of s.enemies) {
        if (e.hp <= 0 || e.y < v.y0 || e.y > v.y1) continue;
        damageEnemy(s, e, imax(1, mulBp(e.maxHp, r.foeBp)), { pid: '', pierce: true });
        burnt = true;
      }
      if (burnt) cue('foeBurn');
      for (const n of [g, d]) {
        if (!n || n.hp <= 0 || n.y < v.y0 || n.y > v.y1) continue;
        n.hp = imax(0, n.hp - r.blows);
        ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
      }
    }
    // The hero: once a blast, whenever it is in the band while it blows.
    for (const h of s.heroes) {
      if (h.downTicks > 0 || h.y < v.y0 || h.y > v.y1 || w.burnt[i] === start) continue;
      w.burnt[i] = start;
      damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'fire', true);
      ev(s, { t: 'senda', kind: 'burn', npc: '', x: h.x, y: h.y, n: i });
      cue('burn');
    }
  }
};

/** A band of nightmares out of a side hole ahead of Grillo (ash wraiths and oozes, by turns). */
const foes = (s: BattleState, r: VentRules, w: VentState): void => {
  if (s.waveT < w.foe || w.end >= 0) return;
  w.foe = s.waveT + r.foes.every;
  const ahead = r.holes.filter((x) => x.row >= w.way + 3 && x.row <= w.way + 9);
  const at = ahead.length ? ahead[rngInt(s.rng, ahead.length)] : r.holes.find((x) => x.row > w.way);
  if (!at) return;
  for (let k = 0; k < r.foes.n; k++) {
    const kind = (w.foes + k) % 3 === 2 ? 'slime' : 'wraith';
    const e = spawnEnemy(s, kind, 1, false, 0, at.x + rngRange(s.rng, -250, 250), at.y + rngRange(s.rng, -250, 250));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
  w.foes += r.foes.n;
  ev(s, { t: 'senda', kind: 'holeOut', npc: '', x: at.x, y: at.y, n: r.foes.n });
};

/** Grillo and Dunna down the road: on while the hero keeps up, short of each vent till its blast ends. */
const walk = (s: BattleState, r: VentRules, w: VentState, cue: (c: string) => void): void => {
  const { g, d } = people(s);
  if (!g) return;
  g.moving = false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const last = r.way.length - 1;
  const p = r.way[imin(last, w.way)] as { x: number; y: number };
  const atEnd = w.way > last;
  // (The hero far behind him up the road: he waits for it. Ahead of him, he goes on to catch up.)
  const behind = !h || (h.y < g.y && !withinRange(h.x, h.y, g.x, g.y, r.wait));
  const mouthAt = atEnd ? r.mouth : p;
  // Crossing a vent: on, whatever comes (never stop inside a band).
  const inBand = ventAt(r, g.y);
  // Nightmares close ahead: they hold back (Dunna shoots) till the hero has dealt with them.
  if (w.cross < 0 && inBand < 0 && s.enemies.some((e) => e.hp > 0 && withinRange(e.x, e.y, g.x, g.y, r.hold))) return;
  if (w.cross < 0 && inBand < 0) {
    // The row short of a vent: wait for its blast to end with enough quiet left.
    const next = w.way <= last ? ventAt(r, p.y) : -1;
    if (next >= 0) {
      const v = r.vents[next] as VentRules['vents'][number];
      const ph = ventPhase(s, v);
      if (ph < r.blow || v.beat - ph < crossTicks(r, v)) return;
      // The hero far behind: wait for it here, short of the vent.
      if (behind) return;
      w.cross = next;
      ev(s, { t: 'senda', kind: 'cross', npc: g.id, x: g.x, y: g.y, n: next });
    } else if (behind) return;
  }
  if (w.cross >= 0 && inBand < 0 && g.y > (r.vents[w.cross] as VentRules['vents'][number]).y1) w.cross = -1;
  g.moving = true;
  if (stepToward(g, mouthAt.x, mouthAt.y, r.pace)) {
    if (atEnd) {
      g.moving = false;
      return;
    }
    w.way++;
    if (w.way * 2 >= r.way.length) cue('half');
    if (r.chalk[w.chalk] !== undefined && w.way > (r.chalk[w.chalk] as number)) {
      w.chalk++;
      ev(s, { t: 'senda', kind: 'chalk', npc: 'dunna', x: g.x, y: g.y, n: w.chalk });
      cue(`chalk${w.chalk}`);
    }
  }
  if (d) {
    // At his side on his row (west of him): never a step behind, so never in a band he is out of.
    d.moving = true;
    stepToward(d, g.x - 650, g.y, r.pace + 30);
  }
};

export const ventTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.vent;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.vent ??= fresh(r));
  if (!s.started) return;
  blasts(s, r, w, cue);
  foes(s, r, w);
  if (w.end < 0) walk(s, r, w, cue);
  const { g, d } = people(s);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
  // At the mouth: the three of them.
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.end < 0 && g && d && h && withinRange(g.x, g.y, r.mouth.x, r.mouth.y, r.reach) && withinRange(d.x, d.y, r.mouth.x, r.mouth.y, r.reach + 600) && withinRange(h.x, h.y, r.mouth.x, r.mouth.y, r.reach + 600)) {
    w.end = s.tick;
    ev(s, { t: 'senda', kind: 'mouth', npc: '', x: r.mouth.x, y: r.mouth.y });
    cue('mouth');
  }
};

/** Grillo's way down, row by row. */
export const ventGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.vent;
  const w = s.senda?.vent;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.end >= 0 ? r.way.length : imin(w.way, r.way.length - 1), need: r.way.length };
};

/** Won a moment after the three are at the Médula's mouth. */
export const ventResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.vent;
  if (!w || w.end < 0) return null;
  return s.tick >= w.end + 24 ? 'won' : null;
};
